import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { AiPlanStepWire, JournalEntry } from '@roll1/shared';
import { useI18n } from '../../i18n';
import type { TranslationKeys } from '../../i18n';
import { useJournalContext } from '../../contexts/JournalContext';
import ConfirmDialog from '../ConfirmDialog';
import ShowMapConfirm from '../ShowMapConfirm';
import type { AiChatMessage, AiOutwardData, AiPlanData } from '../../hooks/useAiChat';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { changeValue, changeFieldName } from './changeFields';

/**
 * What an answer of Tia carries besides her text (group E; mockup studio-ai-chat-preview-v2.html turn.plan,
 * turn.outward, turn.topic, turn.options): the plan with «Do it» (nothing is changed yet), the button that shows to
 * the players / deletes (the AI never does it on its own), the topic line of a remark and its options.
 * Studio's colours (neutral-*, amber-400, blue-300 for Tia, emerald-400 for «the players will see»).
 */

type Translate = (key: keyof TranslationKeys) => string;

const fill = (template: string, values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [k, v]) => text.split(`{${k}}`).join(v), template);

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Read-only preview of actual tool arguments, including multi-field sheet edits and JSON text fields. */
function PlanValue({ value, step, path = '' }: { value: unknown; step: AiPlanStepWire; path?: string }): JSX.Element {
  const { t, locale } = useI18n();
  let parsed = value;
  if (typeof value === 'string' && /^[\[{]/.test(value.trim())) {
    try { parsed = JSON.parse(value); } catch { /* Ordinary text remains readable verbatim. */ }
  }
  if (Array.isArray(parsed)) return <div className="space-y-2">{parsed.length === 0 ? '—' : parsed.map((item, i) => <PlanValue key={i} value={item} step={step} path={path} />)}</div>;
  if (typeof parsed === 'object' && parsed !== null) {
    const fields = Object.entries(parsed as Record<string, unknown>).filter(([key]) => !/(?:Id|Ids)$/.test(key) && !['id', 'characterName'].includes(key));
    return <dl className="space-y-3">{fields.map(([key, item]) => {
      const field = path === 'roleplay_json' ? `${path}.${key}` : key;
      if (['changes', 'data', 'values'].includes(key)) return <PlanValue key={key} value={item} step={step} />;
      // The main was/proposed diff already renders this field in full.
      if (field === step.field) return null;
      const label = changeFieldName({ id: step.id, entity: step.entity ?? { kind: 'journal', id: '', name: '' }, op: 'update', field }, t, locale);
      return <div key={key}><dt className="mb-1 text-meta text-muted-foreground">{label}</dt><dd className="whitespace-pre-wrap break-words"><PlanValue value={item} step={step} path={field} /></dd></div>;
    })}</dl>;
  }
  return <span>{typeof parsed === 'string' ? parsed || '—' : changeValue(parsed, t)}</span>;
}

/** One line of a plan step, from its tool and the thing it names (the server does not word it). */
export function planStepLines(step: AiPlanStepWire, t: Translate, questName: (id: string) => string): string[] {
  const name = step.entity?.name ?? '';
  const args = step.args ?? {};
  switch (step.tool) {
    case 'add_outcome': {
      const reserve = str(args.newReserveTitle) || str((args.newReserve as { label?: unknown } | undefined)?.label);
      const label = str(args.label);
      const lines: string[] = [];
      if (reserve !== '') lines.push(fill(t('ai.plan.step.reserve'), { name: reserve }));
      lines.push(label !== '' ? fill(t('ai.plan.step.outcome'), { label, name }) : fill(t('ai.plan.step.outcomeBare'), { name }));
      return lines;
    }
    case 'add_note': return [fill(t('ai.plan.step.note'), { name })];
    case 'set_objective': {
      const quest = questName(str(args.questId));
      return [quest !== '' ? fill(t('ai.plan.step.objective'), { name, quest }) : fill(t('ai.plan.step.objectiveBare'), { name })];
    }
    case 'set_current_beat': return [fill(t('ai.plan.step.current'), { name })];
    case 'move_beat': return [fill(t('ai.plan.step.move'), { name })];
    case 'equip_armor': case 'set_proficiencies': return [fill(t('ai.plan.step.sheet'), { name })];
    case 'add_chronicle_event': return [fill(t('ai.plan.step.chronicle'), { name })];
    case 'update_journal_entry': return [fill(t('ai.plan.step.edit'), { name })];
    case 'update_character_sheet': return [fill(t('ai.plan.step.sheet'), { name })];
    case 'link_quest': return [fill(t('ai.plan.step.linkQuest'), { name })];
    // A tool this card does not word yet is said as a plain change to the thing it names — never as a raw tool id.
    default: return [fill(t('ai.plan.step.edit'), { name })];
  }
}

/** The ids of the steps that go: the checked ones, minus those whose chain of `dependsOn` has an unchecked step. */
export function effectiveChecked(steps: readonly AiPlanStepWire[], unchecked: ReadonlySet<string>): Set<string> {
  const byId = new Map(steps.map(s => [s.id, s]));
  const ok = (s: AiPlanStepWire, seen = new Set<string>()): boolean => {
    if (unchecked.has(s.id)) return false;
    if (s.dependsOn === undefined) return true;
    if (seen.has(s.id)) return false;
    seen.add(s.id);
    const parent = byId.get(s.dependsOn);
    return parent !== undefined && ok(parent, seen);
  };
  return new Set(steps.filter(s => ok(s)).map(s => s.id));
}

const STALE_KEY = {
  beat_changed: 'ai.plan.stale.beat',
  objective_changed: 'ai.plan.stale.objective',
  missing: 'ai.plan.stale.missing',
  gone: 'ai.plan.stale.gone',
} as const satisfies Record<string, keyof TranslationKeys>;

/** P5: a step failed after it started — the card says how much went back, never «applied». */
const UNKNOWN_KEY = {
  all: 'ai.plan.unknown.all',
  partial: 'ai.plan.unknown.partial',
  none: 'ai.plan.unknown.none',
} as const satisfies Record<string, keyof TranslationKeys>;

export function AiPlanCard({ data, onApply, onDismiss, onHide, busy = false }: {
  data: AiPlanData; onApply: (planId: string, stepIds: string[]) => void;
  onDismiss?: (planId: string) => void; onHide?: () => void; busy?: boolean;
}): JSX.Element | null {
  const { t, locale } = useI18n();
  const journal = useJournalContext();
  const entries = journal.entries as readonly JournalEntry[];
  const questName = useMemo(() => (id: string): string => entries.find(e => e.id === id)?.name ?? '', [entries]);
  const [unchecked, setUnchecked] = useState<ReadonlySet<string>>(new Set());
  const [review, setReview] = useState(false);
  const on = effectiveChecked(data.steps, unchecked);
  const open = data.state === 'open';
  const pending = data.state === 'applying' || data.state === 'dismissing';
  const names = [...new Set(data.steps.map(s => s.entity?.name).filter(Boolean))].join(' · ');
  const title = names || t('ai.plan.title');
  const count = t('ai.results.selected').replace('{n}', String(on.size)).replace('{total}', String(data.steps.length));
  const status = data.state === 'dismissed' ? t('ai.results.declined') : data.state === 'applying' ? t('ai.results.applying') : data.state === 'dismissing' ? t('ai.results.dismissing') : data.state === 'stale' ? t(STALE_KEY[data.staleReason ?? 'missing']) : data.state === 'unknown' ? t(UNKNOWN_KEY[data.rolledBack ?? 'none']) : t('ai.results.pending');
  useEffect(() => { if (data.state === 'applied' || data.state === 'dismissed') setReview(false); }, [data.state]);
  if (data.state === 'applied') return null;
  const apply = () => onApply(data.planId, data.steps.filter(s => on.has(s.id)).map(s => s.id));
  return <>
    <div className={open ? 'rounded-lg border border-hl/30 bg-hl/5 p-3' : 'rounded-md border border-line p-3'} data-testid="ai-plan-card" data-plan-id={data.planId}>
      <p className={open ? 'text-meta text-hl' : 'text-meta text-muted-foreground'} role="status">{status}</p>
      <p className="mt-1 break-words text-ui font-medium">{title}</p>
      {open && <p className="mt-2 text-meta text-muted-foreground">{count}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant={open ? 'default' : 'text'} onClick={() => setReview(true)}>{open ? t('ai.results.review') : t('ai.results.details')}</Button>
        {open && <Button size="sm" variant="outline" disabled={busy || on.size === 0} onClick={apply}>{t('ai.results.apply')}</Button>}
        {open && onDismiss && <Button size="sm" variant="text" disabled={busy} onClick={() => onDismiss(data.planId)}>{t('ai.results.decline')}</Button>}
        {!open && !pending && onHide && <Button size="sm" variant="text" onClick={onHide}>{t('ai.results.hide')}</Button>}
      </div>
    </div>
    <Dialog open={review} onOpenChange={setReview}><DialogContent size="xl" className="flex max-h-[85dvh] flex-col p-5 sm:p-6">
      <DialogHeader className="pr-6"><p className="text-meta text-hl">{status}</p><DialogTitle>{title}</DialogTitle><DialogDescription>{t('ai.results.reviewHint')}</DialogDescription></DialogHeader>
      <div className="min-h-0 overflow-y-auto">
        {data.steps.map(step => {
          const checked = on.has(step.id);
          const dependencyBlocked = !unchecked.has(step.id) && !checked;
          const diff = step.field !== undefined;
          const field = step.field && step.entity ? changeFieldName({ id: step.id, entity: step.entity, op: 'update', field: step.field, before: step.before, after: step.after }, t, locale) : '';
          return <section key={step.id} className="border-b border-line py-4 last:border-0" data-testid="ai-plan-step">
            <label className="flex items-start gap-3 text-ui font-medium"><Checkbox checked={checked} disabled={!open || busy || dependencyBlocked} onCheckedChange={value => setUnchecked(prev => { const next = new Set(prev); if (value === true) next.delete(step.id); else next.add(step.id); return next; })} /><span className="min-w-0 break-words">{planStepLines(step, t, questName).join(' · ')}{field ? ' · ' + field : ''}</span></label>
            {dependencyBlocked && <p className="mt-1 text-meta text-dim">{t('ai.results.dependency')}</p>}
            {diff && <div className={checked ? 'mt-3 grid gap-3 sm:grid-cols-2' : 'mt-3 grid gap-3 opacity-50 sm:grid-cols-2'} data-testid="ai-change-diff"><div className="min-w-0 rounded-md bg-panel-2 p-3"><p className="mb-2 text-meta text-muted-foreground">{t('aiCard.was')}</p><div className="whitespace-pre-wrap break-words text-ui" data-testid="ai-diff-was"><PlanValue value={step.before} step={step} /></div></div><div className="min-w-0 rounded-md border border-ok/20 bg-ok/5 p-3"><p className="mb-2 text-meta text-ok">{t('ai.results.proposed')}</p><div className="whitespace-pre-wrap break-words text-ui" data-testid="ai-diff-now"><PlanValue value={step.after} step={step} /></div></div></div>}
            {step.args && <div className="mt-3 rounded-md bg-panel-2 p-3 text-ui"><PlanValue value={step.args} step={step} /></div>}
          </section>;
        })}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3"><span className="text-meta text-muted-foreground">{open ? count : status}</span><div className="flex flex-wrap gap-2">{open && onDismiss && <Button variant="text" disabled={busy} onClick={() => onDismiss(data.planId)}>{t('ai.results.decline')}</Button>}{open && <Button disabled={busy || on.size === 0} onClick={apply}>{t('ai.results.apply')}</Button>}</div></div>
    </DialogContent></Dialog>
  </>;
}

/** The button in an answer that shows to the players / deletes — after the GM's own confirmation, as the GM's own button does. */
export function AiOutward({ data, onRun }: { data: AiOutwardData; onRun: (actionId: string) => void }): JSX.Element {
  const { t } = useI18n();
  const [asking, setAsking] = useState(false);
  const name = data.target.name;
  // P5: the server's record says what became of it; only a pending record leaves a button.
  const status = data.status ?? 'pending';
  const line = (testId: string, text: string): JSX.Element => <span data-testid={testId} className="inline-flex self-start whitespace-normal text-ui text-hl">{text}</span>;
  if (status === 'running') return <span data-testid="ai-outward-running" className="inline-flex self-start whitespace-normal text-meta text-muted-foreground">{t('ai.outward.running')}</span>;
  if (status === 'unknown') return line('ai-outward-unknown', fill(t('ai.outward.unknown'), { name }));
  if (status === 'partial') return line('ai-outward-partial', fill(t('ai.outward.partial'), { name }));
  if (status === 'failed') return line('ai-outward-failed', fill(t('ai.outward.failed'), { name }));
  if (status === 'stale') return line('ai-outward-stale', t(data.replacedBy !== undefined ? 'ai.outward.changed' : data.note === 'already-satisfied' ? 'ai.outward.already' : 'ai.outward.gone'));
  if (status === 'expired') return line('ai-outward-stale', t('ai.outward.stale'));
  if (data.stale === true && !data.done) {
    return <span data-testid="ai-outward-stale" className="inline-flex self-start whitespace-normal text-ui text-hl">{t('ai.outward.stale')}</span>;
  }
  if (data.done) {
    return (
      <span
        data-testid="ai-outward-done"
        className="inline-flex self-start whitespace-normal rounded-full bg-ok/15 px-2 text-pill text-ok"
      >
        {fill(t(data.kind === 'delete' ? 'ai.outward.doneDelete' : 'ai.outward.doneShow'), { name })}
      </span>
    );
  }
  const isDelete = data.kind === 'delete';
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="ai-outward">
      <Button type="button" size="sm" variant={isDelete ? 'destructive' : 'ok'} onClick={(): void => setAsking(true)}>
        {fill(t(isDelete ? 'ai.outward.delete' : 'ai.outward.show'), { name })}
      </Button>
      <span className="text-meta text-muted-foreground">{data.note === 'persist-failed' ? t('ai.outward.saveFailed') : t(isDelete ? 'ai.outward.deleteCaption' : 'ai.outward.showCaption')}</span>
      {data.fromUntrustedSource !== undefined && <span data-testid="ai-outward-source" className="text-meta text-hl">{t(data.fromUntrustedSource === true ? 'ai.outward.fromSource' : 'ai.outward.sourceUnknown')}</span>}
      {isDelete ? (
        <ConfirmDialog
          open={asking}
          onOpenChange={setAsking}
          title={fill(t('ai.outward.confirmDelete'), { name })}
          tone="danger"
          description={t('ai.outward.confirmDeleteBody')}
          confirmLabel={t('ai.outward.confirmDeleteButton')}
          onConfirm={(): void => onRun(data.actionId)}
        />
      ) : (
        <ShowMapConfirm
          open={asking}
          onOpenChange={setAsking}
          title={fill(t('ai.outward.confirmShow'), { name })}
          description={t('ai.outward.confirmShowBody')}
          confirmLabel={t('ai.outward.confirmShowButton')}
          onConfirm={(): void => onRun(data.actionId)}
        />
      )}
    </div>
  );
}

export interface AiExtraHandlers {
  onApplyPlan?: ((planId: string, stepIds: string[]) => void) | undefined;
  onDismissPlan?: (planId: string) => void;
  onHideResult?: (id: string) => void;
  busy?: boolean;
  onRunConfirmed?: ((actionId: string) => void) | undefined;
  onPickOption?: ((optionsMessageId: string, optionId: string) => void) | undefined;
  onCloseTopic?: ((topicMessageId: string) => void) | undefined;
}

/** The items of an answer that are not text: null when the message is not one of them. */
export function AiExtraItem({ msg, handlers }: { msg: AiChatMessage; handlers: AiExtraHandlers }): JSX.Element | null {
  const { t } = useI18n();
  if (msg.role === 'plan' && msg.planData !== undefined && handlers.onApplyPlan !== undefined) {
    return <AiPlanCard data={msg.planData} onApply={handlers.onApplyPlan} onDismiss={handlers.onDismissPlan} onHide={handlers.onHideResult ? () => handlers.onHideResult?.(`plan:${msg.planData!.planId}`) : undefined} busy={handlers.busy} />;
  }
  if (msg.role === 'outward' && msg.outwardData !== undefined && handlers.onRunConfirmed !== undefined) {
    return <AiOutward data={msg.outwardData} onRun={handlers.onRunConfirmed} />;
  }
  if (msg.role === 'topic' && msg.topicData !== undefined) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-body-s text-muted-foreground" data-testid="ai-topic">
        <span>{fill(t('ai.topic.line'), { title: msg.topicData.title })}</span>
        {msg.topicData.open && handlers.onCloseTopic !== undefined ? (
          <button
            type="button"
            className="text-hl hover:underline"
            onClick={(): void => handlers.onCloseTopic?.(msg.id)}
          >
            {t('ai.topic.close')}
          </button>
        ) : null}
      </div>
    );
  }
  if (msg.role === 'options' && msg.optionsData !== undefined) {
    return (
      <div className="flex flex-wrap gap-2 py-1">
        {msg.optionsData.options.map((o) => (
          <button
            key={o.id}
            type="button"
            data-testid="ai-option"
            className="rounded-full border border-ai/40 bg-panel px-3 py-1 text-body-s text-fg hover:border-ai hover:text-fg"
            onClick={(): void => handlers.onPickOption?.(msg.id, o.id)}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  if (msg.role === 'topic-end') {
    return <div className="text-ui text-ok" data-testid="ai-topic-end">{msg.content}</div>;
  }
  return null;
}
